import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useModalRegistration } from '@/hooks/useModalRegistration';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { validateUserForm, hasAnyError } from './userFormValidators';
import { Textarea } from '@/components/ui/textarea';
import { Role } from '@/types/user';
import { useInviteUser } from '@/hooks/react-query/useUsers';

interface UserInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
  onUserInvited: () => void;
}

export function UserInviteDialog({
  open,
  onOpenChange,
  roles,
  onUserInvited,
}: UserInviteDialogProps) {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    role_ids: [] as string[],
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inviteUserMutation = useInviteUser();

  // Modal registration for the invite dialog
  const { modalProps } = useModalRegistration('user-invite-dialog', open, () =>
    onOpenChange(false)
  );

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();

    setErrors({});

    const validationErrors = validateUserForm({
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      role_ids: formData.role_ids,
    });
    if (hasAnyError(validationErrors)) {
      setErrors(validationErrors as Record<string, string>);
      return;
    }

    try {
      await inviteUserMutation.mutateAsync(formData);

      // Reset form
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        title: '',
        role_ids: [],
        message: '',
      });

      onUserInvited();
    } catch {
      // Error is handled by the mutation hook
      setErrors({ general: 'Failed to invite user. Please try again.' });
    }
  };

  const toggleRole = (roleId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      role_ids: checked
        ? [...prev.role_ids, roleId]
        : prev.role_ids.filter((id) => id !== roleId),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" {...modalProps}>
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation to a new team member to join the platform.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {errors.general && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {errors.general}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      first_name: e.target.value,
                    }))
                  }
                  className={errors.first_name ? 'border-red-500' : ''}
                />
                {errors.first_name && (
                  <p className="text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      last_name: e.target.value,
                    }))
                  }
                  className={errors.last_name ? 'border-red-500' : ''}
                />
                {errors.last_name && (
                  <p className="text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title (Optional)</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="e.g., Fund Accountant"
              />
            </div>

            <div className="space-y-2">
              <Label>Roles</Label>
              <div className="space-y-2 rounded border p-3">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    htmlFor={`role-${role.id}`}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={formData.role_ids.includes(role.id)}
                      onCheckedChange={(checked) =>
                        toggleRole(role.id, checked === true)
                      }
                    />
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: role.color }}
                    />
                    <span>{role.display_name}</span>
                  </label>
                ))}
              </div>
              {errors.role_ids && (
                <p className="text-sm text-red-600">{errors.role_ids}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Personal Message (Optional)</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData((prev) => ({ ...prev, message: e.target.value }))
                }
                placeholder="Add a personal message to the invitation..."
                className="resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={inviteUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={inviteUserMutation.isPending}>
              {inviteUserMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
